import dayjs from "dayjs";
import db from "../db/database";
import { markForDelete, markForSync } from "./syncService";
import { horaAlMinuto } from "./reaperturaDelDia";
import {
  hayCajaAbierta,
  setAperturaActiva,
  clearAperturaActiva,
  clearCajaHistorico,
} from "./cajaEstadoService";
import { getTPVByNoOrId } from "./tpvService";
import { porQueNoSePuedeCobrarEnLaTienda } from "./formaDePagoPorTiendaSyncService";
import { syncProductsStockByLocation } from "./productSyncService";
import { crearRegistro } from "../utils/registro";

const registro = crearRegistro("apertura");

// Servicio para manejar las operaciones relacionadas con apertura de caja

// Dispara en background la sync de stock de la tienda del TPV de esta caja.
// No se espera, para no bloquear la apertura.
//
// Cada salida deja rastro en consola. Antes las tres se iban en silencio y,
// como además no se espera el resultado, no había forma de distinguir "no había
// nada que traer" de "ni siquiera llegó a pedirlo". La salida que conviene
// reconocer es la tienda sin código de almacén: es un dato de BC, no un fallo
// del cliente, y deja el catálogo como estaba sin que nada falle. Hoy las tres
// tiendas del ambiente lo tienen puesto (MALL DEL SOL → "MALL SOL", PVLAY →
// "LA Y", URDESA → "URDESA"), así que no debería aparecer.
const syncStockForApertura = async (tpv) => {
  try {
    if (!tpv) {
      registro.warn("La caja no tiene tienda, no se sincronizan productos");
      return;
    }

    const tpvInfo = await getTPVByNoOrId(tpv);

    if (!tpvInfo) {
      registro.warn(`No se encontró la tienda ${tpv}, no se sincronizan productos`);
      return;
    }

    const locationCode = tpvInfo.locationCode;

    if (!locationCode) {
      registro.warn(
        `La tienda ${tpvInfo.No || tpv} no tiene código de almacén ` +
          "(locationCode) en Business Central, así que no hay stock por tienda que traer"
      );
      return;
    }

    registro.log(`Sincronizando productos del almacén ${locationCode}`);
    const ok = await syncProductsStockByLocation(locationCode);
    if (ok) registro.log(`Productos del almacén ${locationCode} sincronizados`);
    else registro.warn(`No se pudieron sincronizar los productos del almacén ${locationCode}`);
  } catch (error) {
    registro.error("Error al sincronizar stock al abrir caja:", error);
  }
};

/**
 * La apertura de esta caja, en ese día y a esa hora al minuto, si ya la hay.
 *
 * El día es el de la apertura que se va a guardar (hoy, cuando se abre desde el
 * formulario); sin fecha, el de hoy. La hora se compara al minuto porque las
 * aperturas traídas de Business Central la guardan con segundos. Las borradas
 * no cuentan.
 */
const aperturaALaMismaHora = async (caja, fecha, hora) => {
  const minuto = horaAlMinuto(hora);
  if (!caja || !minuto) return null;

  const dia = dayjs(fecha || undefined).format("YYYY-MM-DD");

  return db.aperturaDeCaja
    .where("Caja")
    .equals(caja)
    .filter(
      (apertura) =>
        !apertura.deleted &&
        Boolean(apertura.FechaDeApertura) &&
        dayjs(apertura.FechaDeApertura).format("YYYY-MM-DD") === dia &&
        horaAlMinuto(apertura.HoraDeApertura) === minuto
    )
    .first();
};

// Agregar una nueva apertura de caja a la base de datos
export const addAperturaDeCaja = async (aperturaDeCaja) => {
  try {
    // Verificar si ya hay una caja abierta
    const cajaAbierta = await hayCajaAbierta();
    if (cajaAbierta) {
      throw new Error(
        "Ya existe una caja abierta. Debe cerrar la caja actual antes de abrir una nueva."
      );
    }

    // Obtener la hora actual en formato HH:MM
    const horaActual = new Date().toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    });

    // No se abre dos veces la misma caja el mismo día a la misma hora.
    //
    // Business Central distingue la reapertura de la apertura por la hora: si
    // la segunda llega con la misma `HoraDeApertura` que la primera, ya cerrada,
    // la rechaza con `APERTURA_CERRADA` y la apertura se queda pendiente para
    // siempre. El terminal abre al minuto, así que cerrar y volver a abrir dentro
    // del mismo minuto basta para provocarlo. Con esta guarda, desde un solo
    // equipo no se puede. Acuerdo `contrato-hallazgos-ciclo-cri-2026-09-14.md`,
    // H6 · TPV.
    const horaDeApertura = aperturaDeCaja.HoraDeApertura || horaActual;
    const repetida = await aperturaALaMismaHora(
      aperturaDeCaja.Caja,
      aperturaDeCaja.FechaDeApertura,
      horaDeApertura
    );
    if (repetida) {
      throw new Error(
        `Ya hubo una apertura de esta caja hoy a las ${horaAlMinuto(
          horaDeApertura
        )}. Espera al minuto siguiente para volver a abrirla.`
      );
    }

    // Sin formas de pago configuradas para su tienda, esta caja no puede cobrar.
    //
    // Se comprueba aquí y no en el formulario porque éste es el único paso por
    // el que pasan las tres entradas al alta: la pantalla de aperturas, el panel
    // de inicio y el modal de «Caja no abierta» de facturación.
    //
    // La tienda que decide es **la de la caja**, no la del cajero: Business
    // Central sella la cabecera con `SalesHeaderT.Tienda := Caja."Tienda"`, así
    // que es esa la que `CuentaPago` va a buscar al registrar. Hoy coinciden
    // porque sólo se ofrecen cajas de la tienda del usuario, salvo cuando la
    // del usuario viene vacía -- y ahí se ofrecen todas.
    //
    // Sólo bloquea con `SIN_CONFIGURAR`, nunca con `SIN_DATO`: mientras el
    // servicio web no esté registrado en ese ambiente el terminal no sabe nada,
    // y de no saber no se deduce que falte.
    const motivoSinFormasDePago = await porQueNoSePuedeCobrarEnLaTienda(aperturaDeCaja.TPV);
    if (motivoSinFormasDePago) {
      throw new Error(motivoSinFormasDePago);
    }

    // Agregar la apertura de caja a la base de datos
    const id = await db.aperturaDeCaja.add({
      No: aperturaDeCaja.No || null, // Añadir campo No para Business Central
      Cajero: aperturaDeCaja.Cajero,
      FechaDeApertura: aperturaDeCaja.FechaDeApertura,
      HoraDeApertura: horaDeApertura, // Usar la hora proporcionada o la actual
      ImporteDeApertura: aperturaDeCaja.ImporteDeApertura,
      Estado: aperturaDeCaja.Estado || "Abierto", // Por defecto, el estado es "Abierto"
      Turno: aperturaDeCaja.Turno,
      Caja: aperturaDeCaja.Caja,
      TPV: aperturaDeCaja.TPV,
      createdAt: new Date(),
      pendingSync: 1, // Marcar como pendiente de sincronización
      lastModified: new Date(),
      deleted: false,
    });

    const nuevaApertura = {
      id,
      ...aperturaDeCaja,
      HoraDeApertura: horaDeApertura,
    };

    // Si el estado es "Abierto", establecer como caja activa
    if (nuevaApertura.Estado === "Abierto") {
      await setAperturaActiva(nuevaApertura);
      syncStockForApertura(nuevaApertura.TPV);
    }

    return nuevaApertura;
  } catch (error) {
    registro.error("Error al agregar apertura de caja:", error);
    throw error;
  }
};

// Obtener todas las aperturas de caja
export const getAllAperturasDeCaja = async () => {
  try {
    // Obtener solo aperturas no marcadas como eliminadas
    const aperturasDeCaja = await db.aperturaDeCaja
      .filter((apertura) => !apertura.deleted)
      .toArray();
    return aperturasDeCaja;
  } catch (error) {
    registro.error("Error al obtener aperturas de caja:", error);
    throw error;
  }
};

// Obtener una apertura de caja por ID local
export const getAperturaDeCajaById = async (id) => {
  try {
    const aperturaDeCaja = await db.aperturaDeCaja.get(id);
    if (!aperturaDeCaja || aperturaDeCaja.deleted) {
      throw new Error("Apertura de caja no encontrada");
    }
    return aperturaDeCaja;
  } catch (error) {
    registro.error(`Error al obtener apertura de caja con ID ${id}:`, error);
    throw error;
  }
};

// Obtener una apertura de caja por No de Business Central
export const getAperturaDeCajaByNo = async (no) => {
  try {
    const aperturaDeCaja = await db.aperturaDeCaja
      .where("No")
      .equals(no)
      .and((apertura) => !apertura.deleted)
      .first();

    if (!aperturaDeCaja) {
      throw new Error("Apertura de caja no encontrada");
    }
    return aperturaDeCaja;
  } catch (error) {
    registro.error(`Error al obtener apertura de caja con No ${no}:`, error);
    throw error;
  }
};

// Actualizar una apertura de caja existente
export const updateAperturaDeCaja = async (id, aperturaDeCajaData) => {
  try {
    const aperturaDeCaja = await db.aperturaDeCaja.get(id);
    if (!aperturaDeCaja || aperturaDeCaja.deleted) {
      throw new Error("Apertura de caja no encontrada");
    }

    // No permitir editar si ya tiene número de BC (mismo criterio que
    // deleteCierreDeCaja/updateInvoiceLine)
    if (aperturaDeCaja.No) {
      throw new Error(
        "No se puede editar una apertura de caja que ya ha sido sincronizada con Business Central"
      );
    }

    // Guardar el estado anterior para comparar. También la caja y la tienda:
    // una apertura puede seguir abierta y haber cambiado de caja, y de eso
    // dependen la caja activa, el catálogo de productos y lo que se ve arriba.
    const estadoAnterior = aperturaDeCaja.Estado;
    const cajaAnterior = aperturaDeCaja.Caja;
    const tpvAnterior = aperturaDeCaja.TPV;

    // Actualizar la apertura de caja
    await db.aperturaDeCaja.update(id, {
      ...aperturaDeCajaData,
      lastModified: new Date(),
    });

    // Marcar como pendiente de sincronización
    await markForSync("aperturaDeCaja", id);

    const aperturaActualizada = await getAperturaDeCajaById(id);

    // Si el estado cambió de "Abierto" a otro, limpiar la caja activa
    if (estadoAnterior === "Abierto" && aperturaActualizada.Estado !== "Abierto") {
      await clearAperturaActiva();
    }
    // Si el estado cambió a "Abierto", establecer como caja activa
    else if (estadoAnterior !== "Abierto" && aperturaActualizada.Estado === "Abierto") {
      // Verificar si ya hay otra caja abierta
      const cajaAbierta = await hayCajaAbierta();
      if (cajaAbierta) {
        // Revertir el cambio de estado
        await db.aperturaDeCaja.update(id, {
          Estado: estadoAnterior,
          lastModified: new Date(),
        });
        throw new Error(
          "Ya existe una caja abierta. Debe cerrar la caja actual antes de abrir una nueva."
        );
      }
      await setAperturaActiva(aperturaActualizada);
      syncStockForApertura(aperturaActualizada.TPV);
    }
    // Sigue abierta, pero cambió de caja.
    //
    // Este caso no existía: sólo se miraba el cambio de ESTADO, así que editar
    // la apertura para pasarla a otra caja se guardaba bien, se sincronizaba
    // bien, y todo lo que cuelga de "en qué caja estoy" seguía apuntando a la
    // caja anterior — la caja activa guardada, el indicador de la barra, el
    // vínculo de Traer Histórico y, si además cambió la tienda, el catálogo de
    // productos, que es POR TIENDA.
    else if (estadoAnterior === "Abierto" && aperturaActualizada.Estado === "Abierto") {
      const cambioDeCaja = cajaAnterior !== aperturaActualizada.Caja;
      const cambioDeTienda = tpvAnterior !== aperturaActualizada.TPV;

      if (cambioDeCaja || cambioDeTienda) {
        await setAperturaActiva(aperturaActualizada);
      }

      // El período de Traer Histórico quedó atado a la caja anterior: dejarlo
      // haría que la siguiente sincronización trajera el histórico de una caja
      // en la que ya no se está.
      if (cambioDeCaja) clearCajaHistorico();

      // Los productos sólo se vuelven a traer si cambió la TIENDA. Dos cajas de
      // la misma tienda comparten almacén, así que volver a bajar el catálogo
      // daría exactamente lo mismo que ya está.
      if (cambioDeTienda) syncStockForApertura(aperturaActualizada.TPV);
    }

    return aperturaActualizada;
  } catch (error) {
    registro.error(`Error al actualizar apertura de caja con ID ${id}:`, error);
    throw error;
  }
};

// Eliminar una apertura de caja
export const deleteAperturaDeCaja = async (id) => {
  try {
    const aperturaDeCaja = await db.aperturaDeCaja.get(id);
    if (!aperturaDeCaja) {
      throw new Error("Apertura de caja no encontrada");
    }

    // Si es la caja activa, impedir su eliminación
    if (aperturaDeCaja.Estado === "Abierto") {
      throw new Error(
        "No se puede eliminar una apertura de caja que está actualmente abierta. Cierre la caja primero."
      );
    }

    // No permitir eliminar si ya tiene número de BC (mismo criterio que
    // deleteCierreDeCaja/deleteInvoice) -- validación adicional a la de
    // "caja activa" de arriba, no la reemplaza.
    if (aperturaDeCaja.No) {
      throw new Error(
        "No se puede eliminar una apertura de caja que ya ha sido sincronizada con Business Central"
      );
    }

    // Marcar como eliminado y pendiente de sincronización
    await markForDelete("aperturaDeCaja", id);

    return true;
  } catch (error) {
    registro.error(`Error al eliminar apertura de caja con ID ${id}:`, error);
    throw error;
  }
};

export const getAperturaCaja = async (date, cajero, turn, tpv) => {
  const aperturaDeCaja = await db.aperturaDeCaja
    .where("FechaDeApertura")
    .between(date, date, true, true)
    .toArray();

  return aperturaDeCaja.filter(
    (x) =>
      (!turn || x.Turno === turn) && (!tpv || x.Caja === tpv) && (!cajero || x.Cajero === cajero)
  );
};
